/**
 * ReviewCard — expand/collapse for review body text.
 *
 * Shows the +/- toggle only when the body overflows a 2-line clamp.
 * Uses the `expanded` attribute to drive CSS (line-clamp + icon state).
 *
 * DOM contract:
 *   [data-review-body]   — clamped body text
 *   [data-review-toggle] — expand/collapse button
 */

class ReviewCard extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {boolean} */
  #hasOverflow = false;

  connectedCallback() {
    this.#toggle?.addEventListener("click", this.#handleToggle);
    this.#resizeObserver = new ResizeObserver(() => this.#syncOverflow());
    if (this.#body) this.#resizeObserver.observe(this.#body);
    this.#syncOverflow();
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener("click", this.#handleToggle);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  get #body() {
    return /** @type {HTMLElement | null} */ (this.querySelector("[data-review-body]"));
  }

  get #toggle() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector("[data-review-toggle]"));
  }

  get #expanded() {
    return this.hasAttribute("expanded");
  }

  #syncOverflow = () => {
    const body = this.#body;
    const toggle = this.#toggle;
    if (!body || !toggle) return;

    const wasExpanded = this.#expanded;
    if (wasExpanded) {
      this.removeAttribute("expanded");
      body.classList.add("line-clamp-2");
    }

    this.#hasOverflow = body.scrollHeight > body.clientHeight + 1;

    if (wasExpanded) {
      this.setAttribute("expanded", "");
      body.classList.remove("line-clamp-2");
    }

    const showToggle = this.#hasOverflow || this.#expanded;
    toggle.hidden = !showToggle;
    toggle.classList.toggle("hidden", !showToggle);
  };

  #handleToggle = () => {
    const body = this.#body;
    const toggle = this.#toggle;
    if (!body || !toggle) return;

    const nextExpanded = !this.#expanded;

    if (nextExpanded) {
      this.setAttribute("expanded", "");
      body.classList.remove("line-clamp-2");
      toggle.setAttribute("aria-expanded", "true");
      toggle.setAttribute("aria-label", "Show less");
    } else {
      this.removeAttribute("expanded");
      body.classList.add("line-clamp-2");
      toggle.setAttribute("aria-expanded", "false");
      toggle.setAttribute("aria-label", "Read more");
      this.#syncOverflow();
    }
  };
}

if (!customElements.get("review-card")) {
  customElements.define("review-card", ReviewCard);
}
